import { Divider } from "@mui/material";
import { useCallback, useMemo, type FC, type ReactNode } from "react";

import {
  formatPhoneLocal,
  onActivateKeyDown,
  useDrawers,
  type IPassengerInfo,
} from "@ryde/shared";

import { useDecorateWithMarkLocal } from "../../utils";
import { FramerTooltip } from "../tooltips";
import {
  RidePassengersCsvListWrapper,
  RidePassengersTooltipContentWrapper,
} from "./ride.passengers-csv-list.styled";

const TOOLTIP_MOUTH_LEAVE_DELAY = 300;

interface IRidePassengersCsvList {
  passengersInfo: IPassengerInfo[];
}

export const RidePassengersCsvList: FC<IRidePassengersCsvList> = ({
  passengersInfo,
}) => {
  const { openEditFormDrawer } = useDrawers();
  const { decorateValue } = useDecorateWithMarkLocal();

  const stableDecorateValue = useCallback(
    (value: ReactNode) => decorateValue(value),
    [decorateValue],
  );

  const openEditPassenger = useCallback(
    async (passengerId: number): Promise<void> => {
      if (passengerId) {
        openEditFormDrawer({
          id: passengerId,
          entity: "passenger",
        });
      }
    },
    [openEditFormDrawer],
  );

  const passengersList = useMemo(() => {
    return passengersInfo.map(
      ({ id, firstName, lastName, code, unitName, phone }, index) => {
        const fullName = `${firstName} ${lastName}`;
        const highlightedFullName = stableDecorateValue(fullName);

        const tooltipContent = (
          <RidePassengersTooltipContentWrapper>
            {fullName && (
              <span
                onClick={() => openEditPassenger(id)}
                onKeyDown={onActivateKeyDown(() => openEditPassenger(id))}
                role="button"
                tabIndex={0}
                className="tooltip-content-item full-name"
              >
                {highlightedFullName}
              </span>
            )}
            {code && (
              <>
                <Divider orientation="vertical" className="divider" flexItem />
                <span className="tooltip-content-item">#{code}</span>
              </>
            )}
            {unitName && (
              <>
                <Divider orientation="vertical" className="divider" flexItem />
                <span className="tooltip-content-item">{unitName}</span>
              </>
            )}
            {phone && (
              <>
                <Divider orientation="vertical" className="divider" flexItem />
                <span className="tooltip-content-item">
                  {formatPhoneLocal(phone)}
                </span>
              </>
            )}
          </RidePassengersTooltipContentWrapper>
        );

        return (
          <FramerTooltip
            content={tooltipContent}
            mouseLeaveDelay={TOOLTIP_MOUTH_LEAVE_DELAY}
            key={id}
          >
            <span className="passenger-name">
              {highlightedFullName}
              {index < passengersInfo.length - 1 && ","}
            </span>
          </FramerTooltip>
        );
      },
    );
  }, [openEditPassenger, passengersInfo, stableDecorateValue]);

  return (
    <RidePassengersCsvListWrapper>
      {passengersList}
    </RidePassengersCsvListWrapper>
  );
};
